import axios, { AxiosError } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
const ENDPOINTS = {
  list: `${API_BASE_URL}/customers`,
  byId: (id: string) => `${API_BASE_URL}/customers/${id}`,
  members: (id: string) => `${API_BASE_URL}/customers/${id}/internal-members`,
  member: (id: string, userId: string) => `${API_BASE_URL}/customers/${id}/internal-members/${userId}`,
  projects: (id: string) => `${API_BASE_URL}/customers/${id}/projects`,
  users: (id: string) => `${API_BASE_URL}/customers/${id}/users`,
  user: (id: string, userId: string) => `${API_BASE_URL}/customers/${id}/users/${userId}`,
  invite: (id: string) => `${API_BASE_URL}/customers/${id}/invite`,
  isolationStatus: `${API_BASE_URL}/customers/isolation-status`,
  contacts: (id: string) => `${API_BASE_URL}/customers/${id}/contacts`,
  contact: (id: string, contactId: string) => `${API_BASE_URL}/customers/${id}/contacts/${contactId}`,
  documents: (id: string) => `${API_BASE_URL}/customers/${id}/documents`,
  document: (id: string, docId: string) => `${API_BASE_URL}/customers/${id}/documents/${docId}`,
  documentFile: (id: string, docId: string) => `${API_BASE_URL}/customers/${id}/documents/${docId}/file`,
  communications: (id: string) => `${API_BASE_URL}/customers/${id}/communications`,
  communication: (id: string, commId: string) => `${API_BASE_URL}/customers/${id}/communications/${commId}`,
  crmStatus: `${API_BASE_URL}/customers/crm-status`,
  contracts: (id: string) => `${API_BASE_URL}/customers/${id}/contracts`,
  contract: (id: string, contractId: string) => `${API_BASE_URL}/customers/${id}/contracts/${contractId}`,
  billing: (id: string) => `${API_BASE_URL}/customers/${id}/billing-summary`,
};

export interface Customer {
  id: string;
  company_id: string;
  name: string;
  slug: string;
  description?: string | null;
  logo_url?: string | null;
  industry?: string | null;
  company_size?: string | null;
  website?: string | null;
  phone?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | null;
  secondary_contact_name?: string | null;
  secondary_contact_email?: string | null;
  secondary_contact_phone?: string | null;
  billing_contact_name?: string | null;
  billing_contact_email?: string | null;
  notes?: string | null;
  // Account metadata (Phase 2)
  status?: string | null;            // 'prospect' | 'active' | 'on_hold' | 'churned'
  account_owner_id?: string | null;
  tags?: string | null;              // JSON array string
  account_owner_name?: string | null; // joined
  health_score?: number | null;      // 0–100, computed (Phase 7)
  health_label?: string | null;      // 'healthy' | 'watch' | 'at_risk'
  is_active: boolean;
  is_internal: boolean;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomerWithCounts extends Customer {
  project_count: number;
  internal_member_count: number;
  customer_user_count: number;
}

export interface CustomerInternalMember {
  id: string;
  customer_id: string;
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  assigned_at: string;
  // Engagement metadata + per-person rollups (Phase 3)
  engagement_role?: string | null;
  allocation_percent?: number | null;
  project_count?: number;
  hours_this_month?: number;
}

export interface EngagementInput {
  engagement_role?: string | null;
  allocation_percent?: number | null;
}

export interface CustomerProfileFields {
  description?: string | null;
  logo_url?: string | null;
  industry?: string | null;
  company_size?: string | null;
  website?: string | null;
  phone?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | null;
  secondary_contact_name?: string | null;
  secondary_contact_email?: string | null;
  secondary_contact_phone?: string | null;
  billing_contact_name?: string | null;
  billing_contact_email?: string | null;
  notes?: string | null;
  status?: string | null;
  account_owner_id?: string | null;
  tags?: string[] | string | null;
}

export interface CreateCustomerInput extends CustomerProfileFields {
  name: string;
  slug?: string;
}

export interface UpdateCustomerInput extends CustomerProfileFields {
  name?: string;
  slug?: string;
}

interface APIError { error: string; }

function handleErr(err: unknown): never {
  if (axios.isAxiosError(err)) {
    const ae = err as AxiosError<APIError>;
    throw new Error(ae.response?.data?.error || ae.message);
  }
  throw err;
}

function authHeaders(token: string) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

export async function listCustomers(token: string): Promise<CustomerWithCounts[]> {
  try {
    const r = await axios.get<{ success: boolean; data: CustomerWithCounts[] }>(ENDPOINTS.list, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function getCustomer(id: string, token: string): Promise<Customer> {
  try {
    const r = await axios.get<{ success: boolean; data: Customer }>(ENDPOINTS.byId(id), authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function createCustomer(data: CreateCustomerInput, token: string): Promise<Customer> {
  try {
    const r = await axios.post<{ success: boolean; data: Customer }>(ENDPOINTS.list, data, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function updateCustomer(id: string, data: UpdateCustomerInput, token: string): Promise<Customer> {
  try {
    const r = await axios.put<{ success: boolean; data: Customer }>(ENDPOINTS.byId(id), data, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function deleteCustomer(id: string, token: string): Promise<void> {
  try {
    await axios.delete(ENDPOINTS.byId(id), authHeaders(token));
  } catch (e) { handleErr(e); }
}

export interface CustomerProject {
  id: string;
  name: string;
  project_code: string;
  description?: string | null;
  status: string;
  customer_id: string;
  owner_id: string;
  owner_name?: string | null;
  project_type_name?: string | null;
  member_count: number;
  requirement_count: number;
  approved_count: number;
  rejected_count: number;
  is_active: boolean;
  created_at: string;
}

export async function listCustomerProjects(id: string, token: string): Promise<CustomerProject[]> {
  try {
    const r = await axios.get<{ success: boolean; data: CustomerProject[] }>(ENDPOINTS.projects(id), authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export interface CustomerUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  status: 'active' | 'inactive' | 'pending';
  last_login: string | null;
  created_at: string;
  is_active: boolean;
}

export async function listCustomerUsers(id: string, token: string): Promise<CustomerUser[]> {
  try {
    const r = await axios.get<{ success: boolean; data: CustomerUser[] }>(ENDPOINTS.users(id), authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function inviteCustomerUser(id: string, email: string, token: string): Promise<CustomerUser> {
  try {
    const r = await axios.post<{ success: boolean; data: CustomerUser }>(ENDPOINTS.invite(id), { email }, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export interface CreateCustomerUserInput {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
}

export async function createCustomerUser(id: string, data: CreateCustomerUserInput, token: string): Promise<CustomerUser> {
  try {
    const r = await axios.post<{ success: boolean; data: CustomerUser }>(ENDPOINTS.users(id), data, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function revokeCustomerUser(id: string, userId: string, token: string): Promise<void> {
  try {
    await axios.delete(ENDPOINTS.user(id, userId), authHeaders(token));
  } catch (e) { handleErr(e); }
}

export async function listInternalMembers(id: string, token: string): Promise<CustomerInternalMember[]> {
  try {
    const r = await axios.get<{ success: boolean; data: CustomerInternalMember[] }>(ENDPOINTS.members(id), authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function assignInternalMember(id: string, userId: string, token: string, engagement?: EngagementInput): Promise<void> {
  try {
    await axios.post(ENDPOINTS.members(id), { user_id: userId, ...engagement }, authHeaders(token));
  } catch (e) { handleErr(e); }
}

export async function updateInternalMember(id: string, userId: string, engagement: EngagementInput, token: string): Promise<void> {
  try {
    await axios.put(ENDPOINTS.member(id, userId), engagement, authHeaders(token));
  } catch (e) { handleErr(e); }
}

export async function unassignInternalMember(id: string, userId: string, token: string): Promise<void> {
  try {
    await axios.delete(ENDPOINTS.member(id, userId), authHeaders(token));
  } catch (e) { handleErr(e); }
}

// ---------------------------------------------------------------------------
// Customer Contacts (Phase 2)
// ---------------------------------------------------------------------------
export type ContactType = 'primary' | 'billing' | 'technical' | 'executive' | 'other';
export type PreferredContactMethod = 'email' | 'phone' | 'mobile' | 'other';

export interface CustomerContact {
  id: string;
  company_id: string;
  customer_id: string;
  name?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  department?: string | null;
  linkedin_url?: string | null;
  timezone?: string | null;
  preferred_contact_method?: string | null;
  contact_type: ContactType;
  is_primary: boolean;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContactInput {
  name?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  department?: string | null;
  linkedin_url?: string | null;
  timezone?: string | null;
  preferred_contact_method?: string | null;
  contact_type?: ContactType;
  is_primary?: boolean;
  notes?: string | null;
}

export async function listContacts(id: string, token: string): Promise<CustomerContact[]> {
  try {
    const r = await axios.get<{ success: boolean; data: CustomerContact[] }>(ENDPOINTS.contacts(id), authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function createContact(id: string, data: ContactInput, token: string): Promise<CustomerContact> {
  try {
    const r = await axios.post<{ success: boolean; data: CustomerContact }>(ENDPOINTS.contacts(id), data, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function updateContact(id: string, contactId: string, data: ContactInput, token: string): Promise<CustomerContact> {
  try {
    const r = await axios.put<{ success: boolean; data: CustomerContact }>(ENDPOINTS.contact(id, contactId), data, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function deleteContact(id: string, contactId: string, token: string): Promise<void> {
  try {
    await axios.delete(ENDPOINTS.contact(id, contactId), authHeaders(token));
  } catch (e) { handleErr(e); }
}

// ---------------------------------------------------------------------------
// Customer Documents (Phase 4)
// ---------------------------------------------------------------------------
export type DocumentCategory = 'contract' | 'sow' | 'proposal' | 'deliverable' | 'other';

export interface CustomerDocument {
  id: string;
  company_id: string;
  customer_id: string;
  name: string;
  category: DocumentCategory;
  content_type?: string | null;
  size_bytes?: number | null;
  uploaded_by?: string | null;
  uploaded_by_name?: string | null;
  created_at: string;
}

export async function listDocuments(id: string, token: string): Promise<CustomerDocument[]> {
  try {
    const r = await axios.get<{ success: boolean; data: CustomerDocument[] }>(ENDPOINTS.documents(id), authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function uploadDocument(id: string, file: File, meta: { name?: string; category?: DocumentCategory }, token: string): Promise<CustomerDocument> {
  try {
    const form = new FormData();
    form.append('file', file);
    if (meta.name) form.append('name', meta.name);
    if (meta.category) form.append('category', meta.category);
    const r = await axios.post<{ success: boolean; data: CustomerDocument }>(ENDPOINTS.documents(id), form, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function deleteDocument(id: string, docId: string, token: string): Promise<void> {
  try {
    await axios.delete(ENDPOINTS.document(id, docId), authHeaders(token));
  } catch (e) { handleErr(e); }
}

/** Download a document via an authenticated blob fetch, then trigger a browser save. */
export async function downloadDocument(id: string, doc: CustomerDocument, token: string): Promise<void> {
  try {
    const r = await axios.get(ENDPOINTS.documentFile(id, doc.id), {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(r.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) { handleErr(e); }
}

// ---------------------------------------------------------------------------
// Customer Communications (Phase 5)
// ---------------------------------------------------------------------------
export type CommunicationType = 'call' | 'email' | 'meeting' | 'note';

export interface CustomerCommunication {
  id: string;
  company_id: string;
  customer_id: string;
  communication_type: CommunicationType;
  subject?: string | null;
  body?: string | null;
  participants?: string | null;
  communication_date?: string | null;
  follow_up_date?: string | null;
  follow_up_done: boolean;
  created_by?: string | null;
  created_by_name?: string | null;
  created_at: string;
}

export interface CommunicationInput {
  communication_type?: CommunicationType;
  subject?: string | null;
  body?: string | null;
  participants?: string | null;
  communication_date?: string | null;
  follow_up_date?: string | null;
  follow_up_done?: boolean;
}

export async function listCommunications(id: string, token: string): Promise<CustomerCommunication[]> {
  try {
    const r = await axios.get<{ success: boolean; data: CustomerCommunication[] }>(ENDPOINTS.communications(id), authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function createCommunication(id: string, data: CommunicationInput, token: string): Promise<CustomerCommunication> {
  try {
    const r = await axios.post<{ success: boolean; data: CustomerCommunication }>(ENDPOINTS.communications(id), data, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function updateCommunication(id: string, commId: string, data: CommunicationInput, token: string): Promise<CustomerCommunication> {
  try {
    const r = await axios.put<{ success: boolean; data: CustomerCommunication }>(ENDPOINTS.communication(id, commId), data, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function deleteCommunication(id: string, commId: string, token: string): Promise<void> {
  try {
    await axios.delete(ENDPOINTS.communication(id, commId), authHeaders(token));
  } catch (e) { handleErr(e); }
}

// ---------------------------------------------------------------------------
// Commercials: CRM flag + Contracts + billing (Phase 6)
// ---------------------------------------------------------------------------
export type ContractType = 'retainer' | 'tm' | 'fixed';
export type ContractStatus = 'draft' | 'active' | 'completed' | 'cancelled';

export interface CustomerContract {
  id: string;
  company_id: string;
  customer_id: string;
  name: string;
  contract_type: ContractType;
  status: ContractStatus;
  value?: number | null;
  currency?: string | null;
  budgeted_hours?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  renewal_date?: string | null;
  document_id?: string | null;
  document_name?: string | null;
  notes?: string | null;
  created_at: string;
}

export interface ContractInput {
  name?: string;
  contract_type?: ContractType;
  status?: ContractStatus;
  value?: number | null;
  currency?: string | null;
  budgeted_hours?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  renewal_date?: string | null;
  document_id?: string | null;
  notes?: string | null;
}

export interface BillingSummary {
  hours_total: number;
  hours_this_month: number;
  hours_last_30d: number;
  billable_hours: number;
  revenue_total: number;
  revenue_invoiced: number;
  revenue_uninvoiced: number;
  by_project: { project_id: string; project_name: string; hours: number; revenue: number }[];
}

export async function getCrmStatus(token: string): Promise<{ customer_crm_enabled: boolean }> {
  try {
    const r = await axios.get<{ success: boolean; data: { customer_crm_enabled: boolean } }>(ENDPOINTS.crmStatus, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function updateCrmStatus(enabled: boolean, token: string): Promise<{ customer_crm_enabled: boolean }> {
  try {
    const r = await axios.put<{ success: boolean; data: { customer_crm_enabled: boolean } }>(ENDPOINTS.crmStatus, { enabled }, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function listContracts(id: string, token: string): Promise<CustomerContract[]> {
  try {
    const r = await axios.get<{ success: boolean; data: CustomerContract[] }>(ENDPOINTS.contracts(id), authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function createContract(id: string, data: ContractInput, token: string): Promise<CustomerContract> {
  try {
    const r = await axios.post<{ success: boolean; data: CustomerContract }>(ENDPOINTS.contracts(id), data, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function updateContract(id: string, contractId: string, data: ContractInput, token: string): Promise<CustomerContract> {
  try {
    const r = await axios.put<{ success: boolean; data: CustomerContract }>(ENDPOINTS.contract(id, contractId), data, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function deleteContract(id: string, contractId: string, token: string): Promise<void> {
  try {
    await axios.delete(ENDPOINTS.contract(id, contractId), authHeaders(token));
  } catch (e) { handleErr(e); }
}

export async function getBillingSummary(id: string, token: string): Promise<BillingSummary> {
  try {
    const r = await axios.get<{ success: boolean; data: BillingSummary }>(ENDPOINTS.billing(id), authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function getIsolationStatus(token: string): Promise<{ customer_isolation_enabled: boolean }> {
  try {
    const r = await axios.get<{ success: boolean; data: { customer_isolation_enabled: boolean } }>(ENDPOINTS.isolationStatus, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function updateIsolationStatus(enabled: boolean, token: string): Promise<{ customer_isolation_enabled: boolean }> {
  try {
    const r = await axios.put<{ success: boolean; data: { customer_isolation_enabled: boolean } }>(ENDPOINTS.isolationStatus, { enabled }, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export default {
  listCustomers,
  getCustomer,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  listInternalMembers,
  assignInternalMember,
  updateInternalMember,
  unassignInternalMember,
  listCustomerProjects,
  listCustomerUsers,
  inviteCustomerUser,
  createCustomerUser,
  revokeCustomerUser,
  listContacts,
  createContact,
  updateContact,
  deleteContact,
  listDocuments,
  uploadDocument,
  deleteDocument,
  downloadDocument,
  listCommunications,
  createCommunication,
  updateCommunication,
  deleteCommunication,
  getCrmStatus,
  updateCrmStatus,
  listContracts,
  createContract,
  updateContract,
  deleteContract,
  getBillingSummary,
  getIsolationStatus,
  updateIsolationStatus,
};
